import { useCallback, useEffect, useRef, useState } from "react";

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable ||
    el.getAttribute("role") === "combobox"
  );
}

/**
 * `j` / `k` row navigation with `Enter` to open, over a table that is also fully
 * tab-navigable.
 *
 * Deliberately inert while the focus is in a field: an operator typing "jack"
 * into the search box must not find themselves eight rows down. `/` focuses the
 * search, `Escape` drops the cursor and blurs — the three keys anyone who has
 * used a terminal already knows.
 */
export function useRowNavigation<T>({
  items,
  onOpen,
  getKey,
  enabled = true,
  searchRef,
}: {
  items: T[];
  onOpen: (item: T) => void;
  getKey: (item: T) => string;
  enabled?: boolean;
  /** Focused by `/`. */
  searchRef?: React.RefObject<HTMLInputElement | null>;
}) {
  const [rawCursor, setCursor] = useState<number>(-1);
  const rowRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Clamped on read rather than corrected in an effect: when a tightened filter
  // shrinks the list under the cursor, the very next render must already show a
  // valid row, not a stale index for one frame and then a correction.
  const cursor = rawCursor >= items.length ? items.length - 1 : rawCursor;

  const registerRow = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      if (el) rowRefs.current.set(key, el);
      else rowRefs.current.delete(key);
    },
    [],
  );

  useEffect(() => {
    if (!enabled) return;

    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "/" && !isTypingTarget(e.target)) {
        e.preventDefault();
        searchRef?.current?.focus();
        searchRef?.current?.select();
        return;
      }

      if (e.key === "Escape") {
        if (isTypingTarget(e.target)) (e.target as HTMLElement).blur();
        else setCursor(-1);
        return;
      }

      if (isTypingTarget(e.target)) return;
      if (!items.length) return;

      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        setCursor((c) => Math.min(items.length - 1, c + 1));
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
      } else if (e.key === "Enter" && cursor >= 0 && cursor < items.length) {
        e.preventDefault();
        onOpen(items[cursor]);
      }
    }

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [enabled, items, cursor, onOpen, searchRef]);

  // Keep the cursor row on screen without stealing focus from whatever has it.
  useEffect(() => {
    if (cursor < 0 || cursor >= items.length) return;
    const el = rowRefs.current.get(getKey(items[cursor]));
    el?.scrollIntoView({ block: "nearest" });
  }, [cursor, items, getKey]);

  return {
    cursor,
    setCursor,
    registerRow,
    isCursor: (index: number) => index === cursor,
  };
}
